import m3 from "./m3";

export class Camera {
    x: number;
    y: number;
    zoom: number;
    private canvas: HTMLCanvasElement;
    private viewProjectionMat: number[];
    private minZoom: number;
    private maxZoom: number;

    private velocity: [number, number] = [0, 0]; // Velocity in world units per second
    private animationId: number | null = null;

    // private isTouching: boolean = false;
    // private lastTouchPos: [number, number] | null = null;
    // private lastPinchDist: number | null = null;

    constructor(canvas: HTMLCanvasElement, minZoom: number = 0.5, maxZoom: number = 500) {
        this.canvas = canvas;
        this.x = -canvas.width / 2;
        this.y = -canvas.height / 2;
        this.zoom = 1;
        this.minZoom = minZoom;
        this.maxZoom = maxZoom;
        this.viewProjectionMat = m3.identity();
        this.updateViewProjection();
        this.setupTouchHandlers();
    }

    updateViewProjection(): number[] {
        const projectionMat = m3.projection(this.canvas.width, this.canvas.height);
        const zoomScale = 1 / this.zoom;
        let cameraMat = m3.identity();

        cameraMat = m3.translate(cameraMat, this.x, this.y);
        cameraMat = m3.scale(cameraMat, zoomScale, zoomScale);

        const viewMat = m3.inverse(cameraMat);
        this.viewProjectionMat = m3.multiply(projectionMat, viewMat);
        return this.viewProjectionMat;
    }

    /**
     * Mouse controls
     */

    getClipSpaceMousePosition(e: MouseEvent | WheelEvent): [number, number] {
        const rect = this.canvas.getBoundingClientRect();
        const cssX = e.clientX - rect.left;
        const cssY = e.clientY - rect.top;
        return [(cssX / this.canvas.clientWidth) * 2 - 1, (cssY / this.canvas.clientHeight) * -2 + 1];
    }

    onMouseDown(draw: () => void) {
        return (e: MouseEvent) => {
            e.preventDefault();
            const startInvViewProjMat = m3.inverse(this.viewProjectionMat);
            const startCamera = { x: this.x, y: this.y };
            const startPos = m3.transformPoint(startInvViewProjMat, this.getClipSpaceMousePosition(e));

            const moveCamera = (moveEvent: MouseEvent) => {
                const pos = m3.transformPoint(startInvViewProjMat, this.getClipSpaceMousePosition(moveEvent));
                this.x = startCamera.x + startPos[0] - pos[0];
                this.y = startCamera.y + startPos[1] - pos[1];
                this.updateViewProjection();
                // draw();
            };

            const handleMouseUp = () => {
                window.removeEventListener("mousemove", moveCamera);
                window.removeEventListener("mouseup", handleMouseUp);
            };

            window.addEventListener("mousemove", moveCamera);
            window.addEventListener("mouseup", handleMouseUp);
        };
    }

    onWheel(draw: () => void) {
        return (e: WheelEvent) => {
            e.preventDefault();
            const [clipX, clipY] = this.getClipSpaceMousePosition(e);
            const factor = Math.pow(2, -e.deltaY * 0.01);
            this.adjustZoom(factor, clipX, clipY)
        };
    }

    /**
     * Touch controls
     */

    private setupTouchHandlers(): void {
        let startState: {
            invMat: number[];
            camera: { x: number; y: number };
            pos?: [number, number];
            pinchDist?: number;
            lastTime?: number; // for velocity calculation
            lastPos?: [number, number]; // for velocity calculation
        } | null = null;

        const handleTouchMove = (e: TouchEvent) => {
            e.preventDefault();
            if (!startState) return;

            const currentTime = performance.now();

            if (e.touches.length === 1) { // pan
                const touch = e.touches[0];
                const pos = m3.transformPoint(startState.invMat, this.getClipSpaceTouchPosition(touch));
                this.x = startState.camera.x + (startState.pos![0] - pos[0]);
                this.y = startState.camera.y + (startState.pos![1] - pos[1]);

                if (startState.lastTime && startState.lastPos) {
                    const dt = (currentTime - startState.lastTime) / 1000; // time delta in seconds
                    if (dt > 0) {
                        this.velocity[0] = -(pos[0] - startState.lastPos[0]) / dt;
                        this.velocity[1] = -(pos[1] - startState.lastPos[1]) / dt;
                    }
                }

                // update last position and time
                startState.lastPos = pos;
                startState.lastTime = currentTime;

                this.updateViewProjection();
            } else if (e.touches.length === 2) { // pinch
                const currentPinchDist = this.getPinchDistance(e);
                const pinchRatio = currentPinchDist / startState.pinchDist!;

                const touch1 = e.touches[0];
                const touch2 = e.touches[1];
                const midClipX = (this.getClipSpaceTouchPosition(touch1)[0] + this.getClipSpaceTouchPosition(touch2)[0]) / 2;
                const midClipY = (this.getClipSpaceTouchPosition(touch1)[1] + this.getClipSpaceTouchPosition(touch2)[1]) / 2;

                this.adjustZoom(pinchRatio, midClipX, midClipY);
                startState.pinchDist = currentPinchDist;
            }
        };

        const handleTouchEnd = () => {
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("touchend", handleTouchEnd);
            window.removeEventListener("touchcancel", handleTouchEnd);

            if (startState && startState.pos && (this.velocity[0] !== 0 || this.velocity[1] !== 0)) {
                this.startInertiaAnimation();
            }

            startState = null;
        };

        window.addEventListener("touchstart", (e: TouchEvent) => {
            e.preventDefault();

            if (this.animationId) {
                cancelAnimationFrame(this.animationId);
                this.velocity = [0, 0]
                this.animationId = null;
            }

            startState = {
                invMat: m3.inverse(this.viewProjectionMat),
                camera: { x: this.x, y: this.y },
                lastTime: performance.now(),
            };

            if (e.touches.length === 1) {
                const touch = e.touches[0];
                startState.pos = m3.transformPoint(startState.invMat, this.getClipSpaceTouchPosition(touch));
                startState.lastPos = startState.pos; // Set initial last position
            } else if (e.touches.length === 2) {
                startState.pinchDist = this.getPinchDistance(e);
            }

            window.addEventListener("touchmove", handleTouchMove, { passive: false });
            window.addEventListener("touchend", handleTouchEnd);
            window.addEventListener("touchcancel", handleTouchEnd);
        },
            { passive: false }
        );
    }

    private startInertiaAnimation(): void {
        const friction = 0.95; // controls deceleration speed (0.9 = faster, 0.99 = slower)
        const minVelocity = 0.01; // stop when velocity drops below this

        const animate = () => {
            const dt = 1 / 60; // Fixed time step (assuming 60 FPS)
            this.x += this.velocity[0] * dt;
            this.y += this.velocity[1] * dt;
            this.velocity[0] *= friction;
            this.velocity[1] *= friction;
            this.updateViewProjection();

            // continue animation if velocity is significant
            if (Math.abs(this.velocity[0]) > minVelocity || Math.abs(this.velocity[1]) > minVelocity) {
                this.animationId = requestAnimationFrame(animate);
            } else {
                this.velocity = [0, 0];
                this.animationId = null;
            }
        };

        this.animationId = requestAnimationFrame(animate);
    }

    private getClipSpaceTouchPosition(touch: Touch): [number, number] {
        const rect = this.canvas.getBoundingClientRect();
        const cssX = touch.clientX - rect.left;
        const cssY = touch.clientY - rect.top;
        return [(cssX / this.canvas.clientWidth) * 2 - 1, (cssY / this.canvas.clientHeight) * -2 + 1];
    }

    private getPinchDistance(e: TouchEvent): number {
        if (e.touches.length >= 2) {
            const touch1 = e.touches[0];
            const touch2 = e.touches[1];
            const dx = touch2.clientX - touch1.clientX;
            const dy = touch2.clientY - touch1.clientY;
            return Math.hypot(dx, dy);
        }
        return 0;
    }

    // helper for zoom adjustments (used by both wheel and pinch)
    private adjustZoom(factor: number, clipX: number, clipY: number): void {
        const invMat = m3.inverse(this.viewProjectionMat);
        const preZoom = m3.transformPoint(invMat, [clipX, clipY]);

        const oldZoom = this.zoom;
        this.zoom *= factor;
        this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoom));
        const zoomRatio = oldZoom / this.zoom;

        this.x += (preZoom[0] - this.x) * (1 - zoomRatio);
        this.y += (preZoom[1] - this.y) * (1 - zoomRatio);
        this.updateViewProjection();
    }

    getViewProjectionMat(): number[] {
        return this.viewProjectionMat;
    }

    // world-space bounds of the visible viewport
    getViewportBounds(): [number, number, number, number] {
        const invMat = m3.inverse(this.viewProjectionMat); // Clip to world
        const topLeft = m3.transformPoint(invMat, [-1, 1]); // Top-left (-1, 1 in clip space)
        const bottomRight = m3.transformPoint(invMat, [1, -1]); // Bottom-right (1, -1 in clip space)

        return [topLeft[0], topLeft[1], bottomRight[0], bottomRight[1]];
    };
}